import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import displayINRCurrency from '../helpers/displayCurrency';
import logo from "../assets/logonew.png";

const Invoice = () => {
    const location = useLocation();
    const { order } = location.state || {};
    const navigate = useNavigate();

    if (!order) {
        return <p className="text-center mt-10">No order data available.</p>;
    }

    // Native Browser Print Function
    const handlePrint = () => {
        window.print();
    };

    // Since we are not using a library, "Download" in a web context 
    // without a backend or heavy library is best handled by the Print-to-PDF dialog.
    const handleDownload = () => {
        window.print();
    };

    const handleEdit = (orderid) => {
        navigate('/orders/edit', { state: { orderid } });
    };

    return (
        <div className="min-h-screen bg-gray-50 p-4">
            {/* Action Buttons: Hidden during print */}
            <div className="print:hidden flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-4 mb-8">
                <button
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded shadow-md transition-all"
                    onClick={handlePrint}
                >
                    Print / Save as PDF
                </button>
                <button
                    className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-2 px-6 rounded shadow-md transition-all"
                    onClick={() => handleEdit(order.order_id)}
                >
                    Order Details
                </button>
            </div>

            {/* Invoice Content */}
            <div 
                className="max-w-4xl mx-auto bg-white p-8 border border-gray-200 rounded-lg shadow-sm print:shadow-none print:border-none print:p-0" 
                id="invoice-content"
            >
                {/* Header */}
                <div className="flex flex-col items-center border-b pb-6 mb-6">
                    <div className="flex items-center mb-2">
                        <img src={logo} alt="Firm Logo" className="h-12 w-auto mr-3" />
                        <h1 className="text-4xl font-extrabold text-gray-800">Sridanth</h1>
                    </div>
                    <h2 className="text-xl font-semibold uppercase tracking-widest text-gray-500">Tax Invoice</h2>
                </div>

                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-8 mb-8">
                    <div>
                        <p className="text-sm text-gray-500 uppercase font-bold">Invoice Details</p>
                        <div className="mt-2 space-y-1">
                            <p><span className="font-semibold">Invoice No:</span> {order.order_code}</p>
                            <p><span className="font-semibold">Order ID:</span> {order.order_id}</p>
                            <p><span className="font-semibold">Date:</span> {new Date(order.order_date_time).toLocaleDateString()}</p>
                        </div>
                    </div>
                    <div className="text-right">
                        <p className="text-sm text-gray-500 uppercase font-bold">Billing Date</p>
                        <p className="mt-2 text-lg font-medium">{new Date().toLocaleDateString()}</p>
                    </div>
                </div>

                {/* Address Section */}
                <div className="grid grid-cols-2 gap-8 mb-8">
                    <div className="bg-gray-50 p-4 rounded-md print:bg-white print:border">
                        <h3 className="font-bold text-gray-700 border-b mb-2 pb-1">Bill To:</h3>
                        <p className="font-medium">{order.customer_name}</p>
                        <p className="text-sm text-gray-600 whitespace-pre-line">{order.order_address}</p>
                        <p className="text-sm text-gray-600">{order.country}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-md print:bg-white print:border">
                        <h3 className="font-bold text-gray-700 border-b mb-2 pb-1">Ship To:</h3>
                        <p className="font-medium">{order.customer_name}</p>
                        <p className="text-sm text-gray-600 whitespace-pre-line">{order.order_address}</p>
                        <p className="text-sm text-gray-600">{order.country}</p>
                    </div>
                </div>

                {/* Items Table */}
                <div className="mb-8">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-800 text-white print:bg-gray-100 print:text-black">
                                <th className="p-3 font-semibold">Product Description</th>
                                <th className="p-3 font-semibold text-center">Qty</th>
                                <th className="p-3 font-semibold text-right">Price</th>
                                <th className="p-3 font-semibold text-right">Total</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {order.order_items.map((item, index) => (
                                <tr key={index} className="print:break-inside-avoid">
                                    <td className="p-3">
                                        <p className="font-medium text-gray-800">{item.product_name}</p>
                                        {item.warranty && <p className="text-xs text-gray-500 italic">Warranty: {item.warranty}</p>}
                                    </td>
                                    <td className="p-3 text-center">{item.quantity}</td>
                                    <td className="p-3 text-right">{displayINRCurrency(item.price)}</td>
                                    <td className="p-3 text-right font-medium">{displayINRCurrency(item.subtotal)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Summary Section */}
                <div className="flex justify-end mb-10">
                    <div className="w-full sm:w-64 space-y-2">
                        <div className="flex justify-between text-gray-600">
                            <span>Subtotal:</span>
                            <span>{displayINRCurrency(order.order_amount)}</span>
                        </div>
                        <div className="flex justify-between text-gray-600">
                            <span>Delivery:</span>
                            <span>{displayINRCurrency(order.delivery_charges || 0)}</span>
                        </div>
                        <div className="flex justify-between text-gray-600">
                            <span>GST:</span>
                            <span>{displayINRCurrency(order.gst_charges || 0)}</span>
                        </div>
                        <div className="flex justify-between border-t border-gray-800 pt-2 font-bold text-xl text-gray-900">
                            <span>Total:</span>
                            <span>{displayINRCurrency(order.total_amount)}</span>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="text-center border-t pt-6">
                    <p className="font-bold text-gray-800 mb-2">Thank You for Your Business!</p>
                    <p className="text-[10px] text-gray-500 leading-relaxed max-w-2xl mx-auto">
                        Returns Policy: Items must be returned in original packaging with the invoice. 
                        Terms and conditions apply. Helpline: XXXX XXX XXXX
                    </p>
                </div>
            </div>

            {/* Custom Print Styles */}
            <style dangerouslySetInnerHTML={{ __html: `
                @media print {
                    body { background: white; }
                    .print\\:hidden { display: none !important; }
                    @page { margin: 10mm; }
                    #invoice-content { 
                        border: none !important; 
                        box-shadow: none !important; 
                        width: 100% !important;
                        max-width: none !important;
                    }
                }
            `}} />
        </div>
    );
};

export default Invoice;